import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, DollarSign, Video, CheckCircle2, PackageCheck, Truck, Loader2,
} from "lucide-react";
import api from "@/lib/api";

/** Icon + tone per metric card key. */
const CARD_META = {
  total:                { Icon: Award,        tone: "bg-slate-100 text-slate-900 border-slate-500" },
  payment_needs_review: { Icon: DollarSign,   tone: "bg-amber-100 text-amber-900 border-amber-700" },
  pending_video_review: { Icon: Video,        tone: "bg-amber-100 text-amber-900 border-amber-700" },
  approved:             { Icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-900 border-emerald-700" },
  ready_to_ship:        { Icon: PackageCheck, tone: "bg-sky-100 text-sky-900 border-sky-700" },
  shipped:              { Icon: Truck,        tone: "bg-neutral-100 text-neutral-800 border-neutral-500" },
};

/**
 * Certificate Orders summary section — shown on the Admin Dashboard
 * "Today" tab. Each card links to the Certificate Orders page with the
 * matching `?filter=…` query param already applied. Values come from a
 * single Mongo `$facet` aggregation, not six independent scans.
 */
export default function CertificateOrdersSummary() {
  const [cards, setCards] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/certificate-orders/summary")
      .then((r) => { if (!cancelled) setCards(r.data.cards || []); })
      .catch((e) => {
        if (cancelled) return;
        setError(true);
        // Silent unless the dashboard is truly broken — a toast keeps
        // the failure visible without hiding the rest of the page.
        toast.error("Certificate Orders summary failed to load.");
      });
    return () => { cancelled = true; };
  }, []);

  if (error && !cards) {
    return (
      <div data-testid="dashboard-section-certificate-orders">
        <div className="kicker kicker-red">Certificate Orders</div>
        <div className="text-sm text-[var(--smoke)] mt-2">
          Summary unavailable.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-section-certificate-orders">
      <div className="kicker kicker-red">Certificate Orders</div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-2">
        {(cards || Array(6).fill(null)).map((c, i) => {
          const meta = c ? (CARD_META[c.key] || CARD_META.total) : CARD_META.total;
          const Icon = meta.Icon;
          return (
            <Link
              key={c?.key || i}
              to={`/admin/certificate-orders?filter=${encodeURIComponent(c?.filter || "all")}`}
              className={`text-left p-4 border-2 rounded-2xl transition hover:-translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)] ${meta.tone}`}
              data-testid={`cert-summary-card-${c?.key || i}`}
            >
              <div className="flex items-center justify-between">
                <Icon size={18} />
              </div>
              <div
                className="mt-3 font-display text-4xl leading-none"
                data-testid={c ? `cert-summary-value-${c.key}` : undefined}
              >
                {c ? c.value.toLocaleString()
                    : <Loader2 className="animate-spin inline" size={20} />}
              </div>
              <div className="mt-1 text-xs uppercase tracking-widest font-bold opacity-80">
                {c?.label || "…"}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
